/**
 * Загрузка кабинетов-целей для плиток выбора (`SellerTargetTiles`).
 *
 * Вынесено из мастера «Создать макеты»: тот же список кабинетов нужен мастеру «Создать пряники»
 * (шаги «Визуалы» и «Карточки») и окну «Визуалы и черновики» каталога пряников. Правила отбора прежние:
 * только активные кабинеты, площадка — через реестр (`normalizePipelineMarketplace`), кабинет площадки с
 * выключенной публикацией целью не предлагается, сайт (`site_pt`) — всегда цель.
 */
import { useCallback, useState } from 'react'
import { sellersAPI } from '../lib/api'
import {
  isPipelineTargetMarketplace,
  normalizePipelineMarketplace,
  type PipelineMarketplace,
} from './createDesignsWizard.logic'

export interface SellerTarget {
  seller_id: number
  name: string
  cabinet: string
  marketplace: PipelineMarketplace
  /** Машинный код кабинета (`mp_sellers.cabinet_code`, иначе токен пути визуалов) — им кабинет называют наборы слайдов и запуски воронок. */
  cabinetCode: string | null
}

export type SellerLoadState = 'idle' | 'loading' | 'ready' | 'empty' | 'error'

/** Ключ кабинета в списке выбранных: площадка и id продавца. */
export const sellerTargetKey = (target: Pick<SellerTarget, 'marketplace' | 'seller_id'>): string =>
  `${target.marketplace}:${target.seller_id}`

/** Кабинеты из ответа `GET /sellers`: активные, поддерживаемых площадок. `unsupported` — кабинеты с неизвестной площадкой. */
export function parseSellerTargets(values: unknown): { sellers: SellerTarget[]; unsupported: number } {
  let unsupported = 0
  const sellers = (Array.isArray(values) ? values : [])
    .filter((seller: any) => seller.active ?? seller.is_active ?? true)
    .map((seller: any) => {
      const marketplace = normalizePipelineMarketplace(seller.marketplace || seller.mp)
      if (!marketplace) {
        unsupported += 1
        return null
      }
      if (!isPipelineTargetMarketplace(marketplace)) {
        return null
      }
      const code = String(seller.cabinet_code || seller.visual_path_token || '').trim().toLowerCase()
      return {
        seller_id: Number(seller.seller_id ?? seller.id),
        name: String(seller.seller_name ?? seller.name ?? `Кабинет ${seller.seller_id ?? seller.id}`),
        cabinet: String(seller.seller_short_name ?? seller.short_name ?? seller.seller_id ?? seller.id),
        marketplace,
        cabinetCode: code || null,
      } satisfies SellerTarget
    })
    .filter((seller: SellerTarget | null): seller is SellerTarget =>
      seller !== null && Number.isInteger(seller.seller_id) && seller.seller_id > 0,
    )
  return { sellers, unsupported }
}

export interface SellerTargetsController {
  targets: SellerTarget[]
  loadState: SellerLoadState
  loadError: string
  unsupportedCount: number
  load: () => Promise<void>
  reset: () => void
}

export function useSellerTargets(): SellerTargetsController {
  const [targets, setTargets] = useState<SellerTarget[]>([])
  const [loadState, setLoadState] = useState<SellerLoadState>('idle')
  const [loadError, setLoadError] = useState('')
  const [unsupportedCount, setUnsupportedCount] = useState(0)

  const load = useCallback(async () => {
    setLoadState('loading')
    setLoadError('')
    try {
      const response: any = await sellersAPI.getSellers()
      const values = response?.data?.data ?? response?.data ?? []
      const { sellers, unsupported } = parseSellerTargets(values)
      setTargets(sellers)
      setUnsupportedCount(unsupported)
      setLoadState(sellers.length > 0 ? 'ready' : 'empty')
    } catch {
      setTargets([])
      setLoadState('error')
      setLoadError('Не удалось загрузить кабинеты маркетплейсов.')
    }
  }, [])

  const reset = useCallback(() => {
    setTargets([])
    setLoadState('idle')
    setLoadError('')
    setUnsupportedCount(0)
  }, [])

  return { targets, loadState, loadError, unsupportedCount, load, reset }
}
